import './sentry.ts';
import { ScrollbarWrapper, ToastProvider } from '@scality/core-ui';
import { Layout } from '@scality/core-ui/dist/components/layout/v2/index';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { ThemeProvider } from 'styled-components';
import { GuardianProvider } from './components/GuardianProvider.tsx';
import { Navbar } from './components/Navbar.tsx';
import { DashboardHome, DashboardLayout, DashboardView } from './dashboard';
import { WidgetFocusProvider } from './dashboard/WidgetFocusProvider';
import { AuthGate } from './routes/AuthGate.tsx';
import { ConversationLayout } from './routes/ConversationLayout.tsx';
import { ConversationView } from './routes/ConversationView.tsx';
import { CONVERSATION_PATH, DASHBOARDS_SEGMENT } from './routes/paths.ts';
import './index.css';
import AuthProvider, { isSilentSignInCallback, userManager } from './Auth.tsx';
import { PanelStoreProvider } from './dualPanel';
import { ChatProvider } from './hooks/useChat.ts';
import { McpBridgeProvider } from './mcp/McpBridgeProvider.tsx';
import QueryClientProvider from './QueryClientProvider.tsx';
import { sentryRootErrorHandlers } from './sentry.ts';
import { theme } from './theme.ts';
import { logError } from './utils/logger.ts';

// A hidden-iframe silent sign-in has come back: hand the response to the window that started it.
if (isSilentSignInCallback(window.location.search, window.self !== window.top)) {
  userManager.signinSilentCallback().catch(logError);
} else if (window.opener !== null) {
  // Handle the case where the app is opened in a popup window for authentication callback
  userManager
    .signinPopupCallback()
    .catch((error) => {
      logError(error);
    })
    .finally(() => window.close());
} else {
  ReactDOM.createRoot(document.getElementById('root') as HTMLElement, {
    ...sentryRootErrorHandlers,
  }).render(
    <React.StrictMode>
      <AuthProvider>
        <QueryClientProvider>
          <ThemeProvider theme={theme}>
            <ToastProvider>
              <GuardianProvider>
                <BrowserRouter>
                  <McpBridgeProvider>
                    <WidgetFocusProvider>
                      <ChatProvider>
                        <PanelStoreProvider>
                          <Layout headerNavigation={<Navbar />} variant="transparent">
                            <ScrollbarWrapper>
                              <Routes>
                                <Route element={<AuthGate />}>
                                  <Route element={<ConversationLayout />}>
                                    <Route index element={<ConversationView />} />
                                  </Route>
                                  <Route path={DASHBOARDS_SEGMENT} element={<DashboardLayout />}>
                                    <Route index element={<DashboardHome />} />
                                    <Route path=":templateId" element={<DashboardView />} />
                                  </Route>
                                  <Route path="*" element={<Navigate to={CONVERSATION_PATH} replace />} />
                                </Route>
                              </Routes>
                            </ScrollbarWrapper>
                          </Layout>
                        </PanelStoreProvider>
                      </ChatProvider>
                    </WidgetFocusProvider>
                  </McpBridgeProvider>
                </BrowserRouter>
              </GuardianProvider>
            </ToastProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </AuthProvider>
    </React.StrictMode>,
  );
}
